(() => {
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 240;
  const INITIAL_PRODUCT_LIMIT = 6;
  const MAX_PRODUCT_LIMIT = 10;
  const PAGE_LIMIT = 4;

  const BLOCKED_PAGE_PREFIXES = [
    '/pages/mes-popfolios',
    '/pages/gerer-mes-qr-codes',
    '/pages/mon-espace',
    '/pages/qresult',
    '/pages/creation-pop',
    '/pages/modification-pop',
    '/pages/p/',
    '/pages/pop-',
    '/pages/qr-code/',
    '/customer_authentication/',
    '/account'
  ];

  const TEMPLATE_MARKERS = [
    'template popfolio',
    'templates popfolio',
    'template de popfolio',
    'template de portfolio',
    'popfolio template'
  ];

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (character) => {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };

      return entities[character];
    });

  const normalizePath = (url = '') => {
    try {
      return new URL(url, window.location.origin).pathname.toLowerCase();
    } catch {
      return String(url).split('?')[0].toLowerCase();
    }
  };

  const isBlockedPage = (page) => {
    const path = normalizePath(page?.url);

    return BLOCKED_PAGE_PREFIXES.some((prefix) =>
      path.startsWith(prefix)
    );
  };

  const isPopfolioTemplate = (product) => {
    const haystack = [
      product?.type,
      product?.product_type,
      ...(Array.isArray(product?.tags) ? product.tags : []),
      product?.title,
      product?.handle
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return TEMPLATE_MARKERS.some((marker) =>
      haystack.includes(marker)
    );
  };

  const formatPrice = (value) => {
    const number = Number.parseFloat(value);

    if (!Number.isFinite(number)) {
      return '';
    }

    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(number);
  };

  const imageMarkup = (product) => {
    const image =
      product?.featured_image?.url ||
      product?.image ||
      product?.featured_image ||
      '';

    if (!image) {
      return `
        <span
          class="pf-live-search__page-icon"
          aria-hidden="true"
        >
          ◫
        </span>
      `;
    }

    const alt =
      product?.featured_image?.alt ||
      product?.title ||
      '';

    return `
      <img
        class="pf-live-search__thumb"
        src="${escapeHtml(image)}"
        alt="${escapeHtml(alt)}"
        width="132"
        height="132"
        loading="lazy"
      >
    `;
  };

  const productItem = (product) => {
    const template = isPopfolioTemplate(product);

    const meta = template
      ? 'Template de page Popfolio'
      : product.type || product.vendor || 'Produit';

    const trailing = template
      ? `
        <span class="pf-live-search__badge">
          Template Popfolio
        </span>
      `
      : `
        <span class="pf-live-search__price">
          ${escapeHtml(formatPrice(product.price))}
        </span>
      `;

    return `
      <li
        class="pf-live-search__result"
        role="option"
      >
        <a
          class="pf-live-search__result-link"
          href="${escapeHtml(product.url)}"
          data-pf-search-result
        >
          ${imageMarkup(product)}

          <span class="pf-live-search__copy">
            <span class="pf-live-search__title">
              ${escapeHtml(product.title)}
            </span>

            <span class="pf-live-search__meta">
              ${escapeHtml(meta)}
            </span>
          </span>

          ${trailing}
        </a>
      </li>
    `;
  };

  const pageItem = (page) => `
    <li
      class="pf-live-search__result"
      role="option"
    >
      <a
        class="pf-live-search__result-link"
        href="${escapeHtml(page.url)}"
        data-pf-search-result
      >
        <span
          class="pf-live-search__page-icon"
          aria-hidden="true"
        >
          ↗
        </span>

        <span class="pf-live-search__copy">
          <span class="pf-live-search__title">
            ${escapeHtml(page.title)}
          </span>

          <span class="pf-live-search__meta">
            Page Popfolio
          </span>
        </span>
      </a>
    </li>
  `;

  const groupMarkup = (title, items, renderer) => {
    if (!items.length) {
      return '';
    }

    return `
      <section class="pf-live-search__group">
        <h2 class="pf-live-search__group-title">
          ${escapeHtml(title)}
        </h2>

        <ul class="pf-live-search__list">
          ${items.map(renderer).join('')}
        </ul>
      </section>
    `;
  };

  const initialise = (root) => {
    if (root.dataset.initialized === 'true') {
      return;
    }

    root.dataset.initialized = 'true';

    const sectionId = root.dataset.sectionId;

    const headerSection = document.getElementById(
      `shopify-section-${sectionId}`
    );

    const openButton =
      headerSection?.querySelector('[data-pf-search-open]');

    const closeButtons =
      root.querySelectorAll('[data-pf-search-close]');

    const form =
      root.querySelector('[data-pf-search-form]');

    const input =
      root.querySelector('[data-pf-search-input]');

    const results =
      root.querySelector('[data-pf-search-results]');

    const loader =
      root.querySelector('[data-pf-search-loader]');

    const clearButton =
      root.querySelector('[data-pf-search-clear]');

    if (!openButton || !form || !input || !results) {
      return;
    }

    let debounceTimer = null;
    let requestController = null;
    let activeIndex = -1;
    let lastQuery = '';

    const resultLinks = () =>
      Array.from(
        results.querySelectorAll('[data-pf-search-result]')
      );

    const setExpanded = (expanded) => {
      openButton.setAttribute(
        'aria-expanded',
        String(expanded)
      );

      input.setAttribute(
        'aria-expanded',
        String(expanded)
      );
    };

    const setLoading = (loading) => {
      if (loader) {
        loader.hidden = !loading;
      }

      root.setAttribute(
        'aria-busy',
        String(loading)
      );
    };

    const resetActiveResult = () => {
      activeIndex = -1;

      resultLinks().forEach((link) => {
        link.classList.remove('is-active');
      });
    };

    const showHint = () => {
      results.innerHTML = `
        <div class="pf-live-search__hint">
          Commence à taper au moins ${MIN_CHARS} caractères.
        </div>
      `;

      resetActiveResult();
    };

    const open = () => {
      root.hidden = false;

      document.body.classList.add(
        'pf-search-open'
      );

      setExpanded(true);

      window.requestAnimationFrame(() => {
        input.focus({
          preventScroll: true
        });
      });
    };

    const close = () => {
      requestController?.abort();
      clearTimeout(debounceTimer);

      root.hidden = true;

      document.body.classList.remove(
        'pf-search-open'
      );

      setLoading(false);
      setExpanded(false);
      resetActiveResult();

      openButton.focus({
        preventScroll: true
      });
    };

    const clear = () => {
      requestController?.abort();
      clearTimeout(debounceTimer);

      input.value = '';
      lastQuery = '';

      if (clearButton) {
        clearButton.hidden = true;
      }

      setLoading(false);
      showHint();
      input.focus();
    };

    const renderResults = (products, pages) => {
        const visibleProducts = products.slice(
            0,
            INITIAL_PRODUCT_LIMIT
        );

        const hiddenProducts = products.slice(
            INITIAL_PRODUCT_LIMIT,
            MAX_PRODUCT_LIMIT
        );

        const productsMarkup = visibleProducts.length
            ? `
            <section class="pf-live-search__group">
                <h2 class="pf-live-search__group-title">
                Produits
                </h2>

                <ul class="pf-live-search__list">
                ${visibleProducts
                    .map(productItem)
                    .join('')}
                </ul>

                ${
                hiddenProducts.length
                    ? `
                    <div
                        class="pf-live-search__more-products"
                        data-pf-more-products
                        hidden
                    >
                        <ul class="pf-live-search__list">
                        ${hiddenProducts
                            .map(productItem)
                            .join('')}
                        </ul>
                    </div>

                    <button
                        class="pf-live-search__more-button"
                        type="button"
                        data-pf-show-more
                    >
                        Voir ${hiddenProducts.length} produit${hiddenProducts.length > 1 ? 's' : ''} de plus
                    </button>
                    `
                    : ''
                }
            </section>
            `
            : '';

        const pagesMarkup = groupMarkup(
            'Pages utiles',
            pages,
            pageItem
        );

        results.innerHTML =
            productsMarkup ||
            pagesMarkup
            ? productsMarkup + pagesMarkup
            : `
                <div class="pf-live-search__empty">
                Aucun produit ni aucune page utile ne correspond à cette recherche.
                </div>
            `;

        const showMoreButton =
            results.querySelector('[data-pf-show-more]');

        const moreProducts =
            results.querySelector('[data-pf-more-products]');

        showMoreButton?.addEventListener(
            'click',
            () => {
            moreProducts.hidden = false;
            showMoreButton.remove();
            resetActiveResult();
            }
        );

        resetActiveResult();
        };

    const search = async (query) => {
      requestController?.abort();

      requestController =
        new AbortController();

      setLoading(true);

      const rootUrl =
        window.Shopify?.routes?.root ||
        root.dataset.searchRoot ||
        '/';

      const endpoint = new URL(
        `${rootUrl}search/suggest.json`,
        window.location.origin
      );

      endpoint.searchParams.set(
        'q',
        query
      );

      endpoint.searchParams.set(
        'resources[type]',
        'product,page'
      );

      endpoint.searchParams.set(
        'resources[limit]',
        '10'
      );

      endpoint.searchParams.set(
        'resources[limit_scope]',
        'each'
      );

      endpoint.searchParams.set(
        'resources[options][unavailable_products]',
        'last'
      );

      endpoint.searchParams.set(
        'resources[options][fields]',
        'title,product_type,variants.title,vendor,tag'
      );

      try {
        const response = await fetch(
          endpoint.toString(),
          {
            method: 'GET',
            headers: {
              Accept: 'application/json'
            },
            signal:
              requestController.signal
          }
        );

        if (!response.ok) {
          throw new Error(
            `Recherche Shopify indisponible (${response.status})`
          );
        }

        const payload =
          await response.json();

        if (
          query !==
          input.value.trim()
        ) {
          return;
        }

        const source =
          payload?.resources?.results ||
          {};

        const products =
            Array.isArray(source.products)
                ? source.products.slice(
                    0,
                    MAX_PRODUCT_LIMIT
                )
                : [];
        const pages =
          Array.isArray(source.pages)
            ? source.pages
                .filter(
                  (page) =>
                    !isBlockedPage(page)
                )
                .slice(
                  0,
                  PAGE_LIMIT
                )
            : [];

        renderResults(
          products,
          pages
        );
      } catch (error) {
        if (
          error.name ===
          'AbortError'
        ) {
          return;
        }

        console.error(
          '[Popfolio search]',
          error
        );

        results.innerHTML = `
          <div class="pf-live-search__error">
            La recherche est momentanément indisponible. Réessaie dans quelques secondes.
          </div>
        `;
      } finally {
        if (
          query ===
          input.value.trim()
        ) {
          setLoading(false);
        }
      }
    };

    const scheduleSearch = () => {
      const query =
        input.value.trim();

      if (clearButton) {
        clearButton.hidden =
          query.length === 0;
      }

      clearTimeout(debounceTimer);
      requestController?.abort();

      if (
        query.length <
        MIN_CHARS
      ) {
        lastQuery = '';

        setLoading(false);
        showHint();

        return;
      }

      if (
        query ===
        lastQuery
      ) {
        return;
      }

      debounceTimer =
        window.setTimeout(
          () => {
            lastQuery = query;
            search(query);
          },
          DEBOUNCE_MS
        );
    };

    const moveActive = (
      direction
    ) => {
      const links =
        resultLinks();

      if (!links.length) {
        return;
      }

      links.forEach((link) => {
        link.classList.remove(
          'is-active'
        );
      });

      activeIndex =
        (
          activeIndex +
          direction +
          links.length
        ) %
        links.length;

      const current =
        links[activeIndex];

      current.classList.add(
        'is-active'
      );

      current.scrollIntoView({
        block: 'nearest'
      });
    };

    openButton.addEventListener(
      'click',
      open
    );

    closeButtons.forEach(
      (button) => {
        button.addEventListener(
          'click',
          close
        );
      }
    );

    clearButton?.addEventListener(
      'click',
      clear
    );

    input.addEventListener(
      'input',
      scheduleSearch
    );

    form.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();

        const links =
          resultLinks();

        if (
          activeIndex >= 0 &&
          links[activeIndex]
        ) {
          window.location.assign(
            links[activeIndex].href
          );

          return;
        }

        if (links[0]) {
          window.location.assign(
            links[0].href
          );
        }
      }
    );

    root.addEventListener(
      'keydown',
      (event) => {
        if (
          event.key ===
          'Escape'
        ) {
          event.preventDefault();
          close();

          return;
        }

        if (
          event.key ===
          'ArrowDown'
        ) {
          event.preventDefault();
          moveActive(1);

          return;
        }

        if (
          event.key ===
          'ArrowUp'
        ) {
          event.preventDefault();
          moveActive(-1);

          return;
        }

        if (
          event.key ===
            'Enter' &&
          activeIndex >= 0
        ) {
          const links =
            resultLinks();

          if (
            links[activeIndex]
          ) {
            event.preventDefault();

            window.location.assign(
              links[activeIndex].href
            );
          }
        }
      }
    );

    document.addEventListener(
      'shopify:section:unload',
      (event) => {
        if (
          event.detail?.sectionId ===
          sectionId
        ) {
          requestController?.abort();
          clearTimeout(
            debounceTimer
          );
        }
      }
    );
  };

  const boot = () => {
    document
      .querySelectorAll(
        '[data-pf-live-search]'
      )
      .forEach(initialise);
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      {
        once: true
      }
    );
  } else {
    boot();
  }

  document.addEventListener(
    'shopify:section:load',
    boot
  );
})();